import { GlobalSearchTabPanelProps } from './GlobalSearchTabPanelTypes';
import { useQuery } from '@tanstack/react-query';
import { fetchUnifiedSearch } from '@/lib/api';
import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import useScreenResolution from '@/hooks/useScreenResolution';
import GameCard from '@/components/GameCard/GameCard';
import FileGridItem from '@/components/FileExplorer/FileGridItem';
import {
  SectionBody,
  SectionHeader,
} from '@/components/Section/Section';
import { FormattedMessage } from 'react-intl';
import { getFirstGameByRTP } from '@/lib/game';
import { PartnerSchema, UnifiedSearchResponse } from '@/lib/types';
import { useNavigate } from 'react-router';
import { ga } from '@/lib/analytics';
import { useBasketContext } from '@/contexts/BasketContext';

const AllSearchTabPanel = ({
  query,
  onRequestTabChange,
  showPartnersSection,
  showComplianceDocumentsSection,
  onResultNavigate,
  onResultsLoaded,
}: GlobalSearchTabPanelProps) => {
  const navigate = useNavigate();
  // Category comes from the section being rendered, not the panel - this tab
  // shows both, and the same numeric id can exist in each.
  const { has, toggle } = useBasketContext();
  const trimmedQuery = query.trim();
  const hasSufficientQuery = trimmedQuery.length >= 2;
  const { width } = useScreenResolution();
  const searchLimit = (width ?? 0) > 1469 ? 4 : 3;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['global-search', 'all', trimmedQuery, searchLimit],
    queryFn: () =>
      fetchUnifiedSearch({
        q: trimmedQuery,
        ttype: ['all'],
        limit: searchLimit,
      }),
    enabled: hasSufficientQuery,
  });

  const gameGroups = useMemo(() => {
    const hits = data?.games?.hits;
    if (!hits) return [];
    return Object.values(hits).filter(
      (groupGames) => groupGames && groupGames.length > 0,
    );
  }, [data?.games?.hits]);

  const complianceDocuments = useMemo(
    () =>
      showComplianceDocumentsSection
        ? (data?.compliance_documents?.hits ?? [])
        : [],
    [data?.compliance_documents?.hits, showComplianceDocumentsSection],
  );

  const marketingAssets = useMemo(
    () => data?.marketing_assets?.hits ?? [],
    [data?.marketing_assets?.hits],
  );

  const partners = useMemo(
    () =>
      showPartnersSection
        ? ((data as UnifiedSearchResponse | undefined)?.partners
            ?.hits ?? [])
        : [],
    [data, showPartnersSection],
  );

  const hasAnyResults =
    gameGroups.length > 0 ||
    complianceDocuments.length > 0 ||
    marketingAssets.length > 0 ||
    partners.length > 0;

  const resultCount =
    gameGroups.length +
    complianceDocuments.length +
    marketingAssets.length +
    partners.length;

  useEffect(() => {
    if (!hasSufficientQuery || isLoading || isFetching) return;
    onResultsLoaded?.({
      query: trimmedQuery,
      scope: 'all',
      resultCount,
    });
  }, [
    hasSufficientQuery,
    isFetching,
    isLoading,
    onResultsLoaded,
    resultCount,
    trimmedQuery,
  ]);

  if (!hasSufficientQuery) {
    return (
      <HintText>
        <FormattedMessage
          id="globalSearchTypeMore"
          defaultMessage="Type at least 2 characters to search."
        />
      </HintText>
    );
  }

  return (
    <Root className="wide-scrollbar">
      {(isLoading || isFetching) && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}

      {!isLoading && !isFetching && !hasAnyResults ? (
        <NoResultsText>
          <FormattedMessage
            id="noSearchResults"
            defaultMessage="No search results."
          />
        </NoResultsText>
      ) : null}

      {gameGroups.length > 0 ? (
        <Section>
          <SectionHeader
            title={
              <FormattedMessage id="games" defaultMessage="Games" />
            }
            action={
              <Button
                variant="text"
                onClick={() => onRequestTabChange?.('games')}
              >
                <FormattedMessage
                  id="viewAll"
                  defaultMessage="View All"
                />
              </Button>
            }
          />
          <SectionBody minHeight={420}>
            {gameGroups.slice(0, 4).map((groupGames, index) => {
              const defaultGame =
                getFirstGameByRTP(groupGames) || groupGames[0];
              return (
                <GameCard
                  key={defaultGame.id || index}
                  games={groupGames}
                  defaultGame={defaultGame}
                  selected={false}
                  showDownload
                  section="Global Search All"
                  onDetailsNavigate={() => {
                    ga.trackSearchResultClick({
                      result_type: 'game',
                      result_id: defaultGame.studio_game_id,
                      position: index + 1,
                      search_term: trimmedQuery,
                    });
                    onResultNavigate?.();
                  }}
                />
              );
            })}
          </SectionBody>
        </Section>
      ) : null}

      {complianceDocuments.length > 0 ? (
        <Section>
          <SectionHeader
            title={
              <FormattedMessage
                id="complianceDocuments"
                defaultMessage="Compliance Documents"
              />
            }
            action={
              <Button
                variant="text"
                onClick={() =>
                  onRequestTabChange?.('compliance-documents')
                }
              >
                <FormattedMessage
                  id="viewAll"
                  defaultMessage="View All"
                />
              </Button>
            }
          />
          <SectionBody minHeight={250}>
            {complianceDocuments.slice(0, 5).map((doc, index) => (
              <FileGridItem
                key={doc.id}
                file={doc}
                type="compliance"
                selectionMode="checkbox"
                selected={has('compliance_documents', doc.id)}
                onChange={() => toggle('compliance_documents', doc.id)}
                onOpen={() =>
                  ga.trackSearchResultClick({
                    result_type: 'compliance_document',
                    result_id: doc.id,
                    position: index + 1,
                    search_term: trimmedQuery,
                  })
                }
                exactMatchQuery={trimmedQuery}
              />
            ))}
          </SectionBody>
        </Section>
      ) : null}

      {marketingAssets.length > 0 ? (
        <Section>
          <SectionHeader
            title={
              <FormattedMessage
                id="marketingAssets"
                defaultMessage="Marketing Assets"
              />
            }
            action={
              <Button
                variant="text"
                onClick={() =>
                  onRequestTabChange?.('marketing-assets')
                }
              >
                <FormattedMessage
                  id="viewAll"
                  defaultMessage="View All"
                />
              </Button>
            }
          />
          <SectionBody minHeight={287}>
            {marketingAssets.slice(0, 5).map((asset, index) => (
              <FileGridItem
                key={asset.id}
                file={asset}
                type="marketing"
                selectionMode="checkbox"
                selected={has('marketing_assets', asset.id)}
                onChange={() => toggle('marketing_assets', asset.id)}
                onOpen={() =>
                  ga.trackSearchResultClick({
                    result_type: 'marketing_asset',
                    result_id: asset.id,
                    position: index + 1,
                    search_term: trimmedQuery,
                  })
                }
              />
            ))}
          </SectionBody>
        </Section>
      ) : null}

      {partners.length > 0 ? (
        <Section>
          <SectionHeader
            title={
              <FormattedMessage
                id="partners"
                defaultMessage="Partners"
              />
            }
            action={
              <Button
                variant="text"
                onClick={() => onRequestTabChange?.('partners')}
              >
                <FormattedMessage
                  id="viewAll"
                  defaultMessage="View All"
                />
              </Button>
            }
          />
          <PartnersGrid>
            {partners.slice(0, 8).map((partner, index) => (
              <PartnerMiniCard
                key={partner.id}
                partner={partner}
                onClick={() => {
                  ga.trackSearchResultClick({
                    result_type: 'partner',
                    result_id: partner.id,
                    position: index + 1,
                    search_term: trimmedQuery,
                  });
                  onResultNavigate?.();
                  navigate(`/admin/partners/${partner.id}`);
                }}
              />
            ))}
          </PartnersGrid>
        </Section>
      ) : null}
    </Root>
  );
};

export default AllSearchTabPanel;

const PartnerMiniCard = ({
  partner,
  onClick,
}: {
  partner: PartnerSchema;
  onClick: () => void;
}) => {
  return (
    <PartnerCardRoot
      role="link"
      tabIndex={0}
      aria-label={partner.name}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <PartnerName title={partner.name}>{partner.name}</PartnerName>
    </PartnerCardRoot>
  );
};

const Root = styled('div')(({ theme }) => ({
  padding: `0 ${theme.spacing(1)} ${theme.spacing(2)}`,
}));

const Section = styled('section')({
  width: '100%',
});

const LoaderContainer = styled('div')({
  width: '100%',
  padding: '40px',
  display: 'flex',
  justifyContent: 'center',
});

const NoResultsText = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));

const HintText = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));

const PartnersGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const PartnerCardRoot = styled('div')(({ theme }) => ({
  minWidth: 180,
  maxWidth: 260,
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.radii.xs,
  backgroundColor: theme.palette.white,
  boxShadow:
    '0px 10px 32px -4px #1319271A, 0px 6px 14px -6px #1319271F',
  border: `1px solid ${theme.palette.grey100}`,
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.grey200,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.red600}`,
    outlineOffset: 2,
  },
}));

const PartnerName = styled('div')(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));
